import { z } from 'zod';
import { paginationSchema, sortOrderSchema } from './helpers';
import { knownTotalParamsSchema, knownTotalQuerySchema } from './known-total';
import { severitySchema } from './shared/severity';
import { catalogFrameworkSchema, catalogIdSchema, } from './ai-instruction-catalog';
import { policyKeySchema, policyModeSchema } from './policies';
import { ColumnDataTypes, FiltersSchema, TableResponseMetaSchema, tableResponsePaginationSchema, createTableQuerySchema, } from './table';
/**
 * Basic user info attached to a project row (users who have the project on a
 * machine that reported it).
 */
export const projectUserBasicSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    /** Most recent time this user was seen in the project (ISO timestamp). */
    lastSeenAt: z.string(),
});
/**
 * A project row in the Inventory → Projects DataTable.
 *
 * `repoOriginUrl` is null for repo-less folder-identity projects.
 * `aiInstructionsCount` / `hooksCount` are derived from `file_ownership`
 * joined on `project_id` (file_type = 'ai_instruction' / 'hook').
 */
export const projectDataTableItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    repoOriginUrl: z.string().nullable(),
    users: z.array(projectUserBasicSchema),
    userCount: z.number(),
    aiInstructionsCount: z.number(),
    hooksCount: z.number(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
});
/**
 * Single-project detail (the `/inventory/projects/:slug` page).
 */
export const projectDetailSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    repoOriginUrl: z.string().nullable(),
    machineId: z.string().nullable(),
    users: z.array(projectUserBasicSchema),
    aiInstructionsCount: z.number(),
    hooksCount: z.number(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
});
// ============================================================================
// Security coverage (single-project page — KIR-3517)
// ============================================================================
/**
 * How well a project's AI instruction files cover a catalog category. `null`
 * on the page means "not scanned yet" — the instruction-file scanner (KIR-3516)
 * that computes these has not run (no coverage row for the category).
 */
export const coverageStatusSchema = z.enum(['covered', 'partial', 'missing']);
/**
 * One row of the single-project coverage table: a catalog category plus its
 * coverage status for this project. `status: null` / `coveredFilePath: null`
 * = not scanned yet.
 */
export const projectCoverageCategorySchema = z.object({
    /** Catalog category id (built-in slug or custom uuid). */
    id: z.string(),
    framework: catalogFrameworkSchema,
    name: z.string(),
    severity: severitySchema,
    /** Framework reference, e.g. `A03:2021` / `AML.T0051` / `PW.5`. */
    reference: z.string().nullable(),
    description: z.string(),
    /** Example instruction to add when this category is missing. */
    instruction: z.string(),
    status: coverageStatusSchema.nullable(),
    /** Instruction file that covers this category (when covered/partial). */
    coveredFilePath: z.string().nullable(),
});
/** Covered / partial / missing / not-scanned counts for a set of categories. */
export const coverageCountsSchema = z.object({
    covered: z.number().int().nonnegative(),
    partial: z.number().int().nonnegative(),
    missing: z.number().int().nonnegative(),
    /** Categories with no coverage result yet (scanner hasn't run). */
    notScanned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
/**
 * One policy domain on the coverage page: an AI-instruction catalog rendered as
 * a selectable card (title + Off/Monitor/Enforce mode badge + count summary) and
 * its category table. The set of domains is dynamic — one per catalog whose
 * policy exists — so a new catalog (KIR-3925) appears automatically via
 * {@link CATALOG_POLICY_KEY}.
 */
export const projectCoverageDomainSchema = z.object({
    /** Catalog id, e.g. `secure_coding` / `agent_instructions`. */
    id: catalogIdSchema,
    /** Human catalog title, e.g. "Secure AI Coding Enforcement". */
    title: z.string(),
    /** Policy controlling this catalog's mode. */
    policyKey: policyKeySchema,
    /** Current Off / Monitor / Enforce mode for the card badge. */
    mode: policyModeSchema,
    summary: coverageCountsSchema,
    categories: z.array(projectCoverageCategorySchema),
});
/**
 * Aggregate summary tiles at the top of the coverage page (no score): the
 * catalog counts summed across all domains, plus the count of critical-severity
 * gaps (missing categories rated Critical).
 */
export const projectCoverageSummarySchema = coverageCountsSchema.extend({
    /** Missing categories with `severity: critical` — the "Critical gaps" tile. */
    criticalGaps: z.number().int().nonnegative(),
});
/**
 * Full payload for the single-project security-coverage page: project metadata,
 * the last scan time (null until the scanner runs), the aggregate summary, and
 * one domain per AI-instruction catalog (each with its own mode + category
 * table).
 */
export const projectCoverageSchema = z.object({
    project: projectDetailSchema,
    /** ISO timestamp of the most recent coverage scan; null if never scanned. */
    lastScannedAt: z.string().nullable(),
    summary: projectCoverageSummarySchema,
    domains: z.array(projectCoverageDomainSchema),
});
/**
 * Response of the coverage re-scan endpoint: the scan runs asynchronously
 * (pg-boss), so a 202 with `queued: true` is all the caller gets — the page
 * re-fetches coverage to observe the result.
 */
export const projectCoverageRescanResponseSchema = z.object({
    queued: z.boolean(),
});
// ============================================================================
// DataTable schemas
// ============================================================================
/**
 * Filterable fields for the projects DataTable.
 */
export const PROJECT_FILTERABLE_FIELDS = {
    NAME: 'name',
    REPO_URL: 'repoUrl',
    USERS: 'users',
};
/**
 * Column data types for projects DataTable fields. Single source of truth for
 * allowed filter fields and their valid operators (see createTableQuerySchema).
 */
export const PROJECT_COLUMN_DATA_TYPES = {
    name: ColumnDataTypes.STRING,
    repoUrl: ColumnDataTypes.STRING,
    users: ColumnDataTypes.ENUM,
};
/**
 * Sort fields for the projects DataTable.
 */
export const projectDataTableSortFieldSchema = z.enum([
    'name',
    'repoUrl',
    'users',
    'aiInstructions',
    'hooks',
    'lastSeenAt',
]);
/**
 * Backend query schema for the projects DataTable.
 * Handles bracket-notation filter params from Fastify OR a pre-parsed filters array.
 */
const projectDataTableQueryBaseSchema = paginationSchema.extend({
    sortBy: projectDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    knownTotal: knownTotalQuerySchema,
});
export const projectDataTableQuerySchema = createTableQuerySchema(projectDataTableQueryBaseSchema, PROJECT_COLUMN_DATA_TYPES);
/**
 * Response schema for the projects DataTable endpoint.
 * Shape: { data: ProjectDataTableItem[], pagination: {...}, meta: {...} }
 */
export const projectTableResponseSchema = z.object({
    data: z.array(projectDataTableItemSchema),
    pagination: tableResponsePaginationSchema,
    meta: TableResponseMetaSchema,
});
/**
 * API client params for the projects DataTable endpoint.
 * page and limit are optional here; the backend applies defaults.
 */
export const listProjectsDataTableParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    sortBy: projectDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    filters: z.array(FiltersSchema.element).optional(),
    knownTotal: knownTotalParamsSchema,
});
// ============================================================================
// Filter options (dynamic dropdowns for the name + repo URL columns)
// ============================================================================
/**
 * String columns that expose a dynamic filter-options dropdown (distinct
 * values) on the Projects DataTable — mirrors the Extensions "name" filter.
 */
export const PROJECT_FILTER_OPTION_FIELDS = {
    NAME: 'name',
    REPO_URL: 'repoUrl',
};
export const projectFilterOptionFieldSchema = z.enum(['name', 'repoUrl']);
export const projectFilterOptionsQuerySchema = z.object({
    field: projectFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(10),
    offset: z.coerce.number().int().min(0).default(0),
});
export const projectFilterOptionsResponseSchema = z.object({
    field: projectFilterOptionFieldSchema,
    options: z.array(z.string()),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
//# sourceMappingURL=projects.js.map